/**
 * Fatiado de lib/api.ts por domínio (#131, regra R3).
 */
import type { I18nRunWithTenant, I18nRunLog } from './i18n.js'
import type {
  CountryDto,
  BillingSettingsDto,
  PlanDto,
  LeadStatus,
  LeadPlan,
  LeadDto,
} from './billing.js'
import { get, post, put, patch, del, request } from './client.js'
// ─── Platform ─────────────────────────────────────────────────────────────────

export type Tenant = {
  id: string
  slug: string
  name: string
  status: 'active' | 'inactive' | 'deleted'
  country_code: string
  locale: string
  custom_domain: string | null
  created_at: number
  updated_at: number
}

export type PlatformUser = {
  id: string
  email: string | null
  role: string
  roles?: string[]
  display_name: string | null
  is_active: boolean
  created_at: number
}

/**
 * Equipe do restaurante atual (X-Tenant-Slug). Para o DONO: as rotas
 * /platform/* são só de platform_owner e davam 403 ao convidar (#266/#156).
 */
export const staff = {
  list: () =>
    get<{
      tenant: { id: string; slug: string; name: string }
      users: PlatformUser[]
      /** vagas por role (#284 multi-role); limit null = ilimitado */
      seats?: Record<string, { used: number; limit: number | null }>
    }>('/admin/users'),
  invite: (body: { email: string; role?: string; roles?: string[]; display_name?: string }) =>
    post<{ id: string; email: string; roles: string[] }>('/admin/users', body),
  setActive: (userId: string, isActive: boolean) =>
    request<{ id: string; is_active: boolean }>('PATCH', `/admin/users/${userId}`, {
      is_active: isActive,
    }),
  setRoles: (userId: string, roles: string[]) =>
    request<{ id: string; roles: string[] }>('PUT', `/admin/users/${userId}/roles`, { roles }),
}

export const platform = {
  listTenants: () => get<Tenant[]>('/platform/tenants'),
  createTenant: (body: {
    slug: string
    name: string
    country_code: string
    locale: string
    owner_email?: string
  }) => post<Tenant>('/platform/tenants', body),
  updateTenant: (id: string, body: { name?: string; status?: 'active' | 'inactive' }) =>
    request<Tenant>('PATCH', `/platform/tenants/${id}`, body),
  listTenantUsers: (tenantId: string) => get<PlatformUser[]>(`/platform/tenants/${tenantId}/users`),
  inviteTenantUser: (
    tenantId: string,
    body: { email: string; role?: string; roles?: string[]; display_name?: string },
  ) =>
    post<{ id: string; email: string; roles: string[] }>(
      `/platform/tenants/${tenantId}/users`,
      body,
    ),
  deactivateTenantUser: (tenantId: string, userId: string) =>
    del<{ message: string }>(`/platform/tenants/${tenantId}/users/${userId}`),
  /** Ativar/desativar usuário do restaurante (PATCH — caminho completo). */
  setTenantUserActive: (tenantId: string, userId: string, isActive: boolean) =>
    request<{ id: string; is_active: boolean }>(
      'PATCH',
      `/platform/tenants/${tenantId}/users/${userId}`,
      { is_active: isActive },
    ),
  /** Ativar/desativar usuário DA PLATAFORMA (platform_owner). */
  setUserActive: (userId: string, isActive: boolean) =>
    request<{ id: string; is_active: boolean }>('PATCH', `/platform/users/${userId}`, {
      is_active: isActive,
    }),
  /** Toggles de features por tenant (migration 0039). */
  /** Bundles: catálogo de planos (platform_owner). */
  listPlans: () => get<PlanDto[]>('/platform/plans'),
  updatePlan: (
    code: string,
    body: Partial<{
      name: string
      description: string | null
      price_cents: number
      currency: string
      stripe_price_id: string | null
      max_kitchen: number | null
      max_waiters: number | null
      max_managers: number | null
      translation_locales: number | null
      is_active: boolean
    }>,
  ) => put<PlanDto>(`/platform/plans/${code}`, body),
  listTenantFeatures: (tenantId: string) =>
    get<{ feature: 'ai_translation' | 'menu_import_ocr' | 'promotions'; enabled: boolean }[]>(
      `/platform/tenants/${tenantId}/features`,
    ),
  setTenantFeature: (
    tenantId: string,
    feature: 'ai_translation' | 'menu_import_ocr' | 'promotions',
    enabled: boolean,
  ) =>
    put<{ feature: string; enabled: boolean }>(`/platform/tenants/${tenantId}/features`, {
      feature,
      enabled,
    }),
  provisionDns: (id: string) =>
    post<{ domain: string }>(`/platform/tenants/${id}/provision-dns`, {}),
  /** #293: países (cadastro do bundle / versão completa homologada). */
  listCountries: () => get<CountryDto[]>('/platform/countries'),
  updateCountry: (
    code: string,
    body: Partial<{ enabled_signup: boolean; homologated_full: boolean }>,
  ) => put<CountryDto>(`/platform/countries/${code}`, body),
  /** #293: config de billing (trial, carência, plano default). */
  billingSettings: () => get<BillingSettingsDto>('/platform/billing/settings'),
  updateBillingSettings: (body: Partial<BillingSettingsDto>) =>
    put<BillingSettingsDto>('/platform/billing/settings', body),
  /** #293: leads de contato (Small/Pro "Mais informações", avise-me). */
  listLeads: (filter: { status?: LeadStatus; plan?: LeadPlan; country?: string } = {}) => {
    const q = new URLSearchParams()
    if (filter.status) q.set('status', filter.status)
    if (filter.plan) q.set('plan', filter.plan)
    if (filter.country) q.set('country', filter.country)
    const qs = q.toString()
    return get<LeadDto[]>(`/platform/leads${qs ? `?${qs}` : ''}`)
  },
  setLeadStatus: (id: string, status: LeadStatus) =>
    request<LeadDto>('PATCH', `/platform/leads/${id}`, { status }),
  /** Botão "traduções da base": dicionário de UI da plataforma (ui_texts). */
  refreshUiTexts: (force = false) =>
    post<{ started: boolean; bases: string[] }>('/platform/i18n/ui-texts/refresh', { force }),
  /** Botão por restaurante: re-traduz o cardápio do tenant informado. */
  refreshTenantI18n: (tenantId: string) =>
    post<{ started: boolean; invalidated: number }>(
      `/platform/tenants/${tenantId}/i18n/refresh`,
      {},
    ),
  /** Ideias do concierge: conversas do chat institucional (resumo/tags). */
  conciergeConversations: () =>
    get<
      Array<{
        id: string
        locale: string
        messageCount: number
        summary: string | null
        tags: string | null
        ideas: string | null
        finishedAt: number | null
        createdAt: number
      }>
    >('/platform/concierge'),
  /** Conversa completa do concierge (mensagens). */
  conciergeConversation: (id: string) =>
    get<{
      conversation: { id: string; locale: string; summary: string | null; createdAt: number }
      messages: Array<{
        id: string
        role: 'user' | 'assistant'
        content: string
        createdAt: number
      }>
    }>(`/platform/concierge/${id}`),
  /** Mensagens do formulário de contato do site (caixa de entrada). */
  contactMessages: () =>
    get<
      Array<{
        id: string
        name: string
        email: string
        message: string
        locale: string
        handledAt: number | null
        createdAt: number
      }>
    >('/platform/contact'),
  /** Alterna tratada/reabrir uma mensagem de contato. */
  contactSetHandled: (id: string) =>
    patch<{ id: string; handledAt: number | null }>(`/platform/contact/${id}`, {}),
  /** Cobertura do dicionário de interface por idioma (tela de Traduções). */
  uiTextsCoverage: () =>
    get<Array<{ base: string; native: string; rtl: boolean; total: number; translated: number }>>(
      '/platform/i18n/ui-texts/coverage',
    ),
  /** Execuções de todos os escopos/tenants (status ao vivo, polling). */
  i18nRuns: () => get<I18nRunWithTenant[]>('/platform/i18n/runs'),
  /** Eventos de uma execução (qualquer tenant). */
  i18nRunLogs: (runId: string) => get<I18nRunLog[]>(`/platform/i18n/runs/${runId}/logs`),
  listUsers: () => get<PlatformUser[]>('/platform/users'),
  inviteUser: (body: { email: string; display_name?: string }) =>
    post<{ id: string; email: string; role: string }>('/platform/users', body),
  deleteUser: (id: string) => del<{ message: string }>(`/platform/users/${id}`),
}

// ─── Sessão da plataforma (#430) ──────────────────────────────────────────────

export type ImpersonateStartResult = {
  access_token: string
  expires_at: number
  impersonating: {
    tenant_id: string
    tenant_slug: string
    tenant_name: string
    restaurant_owner_id: string | null
  }
}

export type ImpersonateStopResult = {
  access_token: string
  expires_at: number
  already_stopped: boolean
}

export const impersonate = {
  start: (body: { tenant_id: string; reason: string }) =>
    post<ImpersonateStartResult>('/platform/impersonate/start', body),
  stop: () => post<ImpersonateStopResult>('/platform/impersonate/stop', {}),
}

export type PlatformAccessItem = {
  session_id: string
  started_at: number
  ended_at: number | null
  expires_at: number
  ended_by: 'stop' | 'expired'
  reason: string
}

export const platformAccess = {
  list: (q?: { cursor?: string; limit?: number }) => {
    const params = new URLSearchParams()
    if (q?.cursor) params.set('cursor', q.cursor)
    if (q?.limit !== undefined) params.set('limit', String(q.limit))
    const qs = params.toString()
    return get<{ items: PlatformAccessItem[]; next_cursor: string | null }>(
      `/admin/platform-access${qs ? `?${qs}` : ''}`,
    )
  },
}
